import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Aviso, EstadoCarregando, EstadoErro, EstadoVazio } from '../componentes/ui';
import { useIdentidade } from '../hooks/useSessao';
import { obterEmpresa } from '../servicos/empresas';
import { requisicaoAutenticada } from '../servicos/http';
import { formatarCnpj } from '../servicos/formato';
import { Pagina } from './Carteira';
import { DesagioDoAssessor } from './DesagioDoAssessor';
import { ClientesDoParticipante, RelatorioDoParticipante } from './RelatorioDoParticipante';
import { AbaCadastro } from './cliente/AbaCadastro';
import { AbaColaboradores } from './cliente/AbaColaboradores';
import { AbaServicos } from './cliente/AbaServicos';
import { BotaoDoCrm } from './crm/BotaoDoCrm';
import { ORIGENS, PARAMETRO_DE_ORIGEM, origemDaQuery } from './origem-da-empresa';

type Aba = 'cadastro' | 'servicos' | 'colaboradores' | 'desagio' | 'relatorio' | 'clientes';

interface PapeisDeIcms {
  readonly papeis: { cliente: boolean; assessor: boolean; fornecedor: boolean };
}

/**
 * Cadastro completo de uma empresa.
 *
 * ## As abas
 *
 * Cadastro (identificação, endereço, banco, documentos, Drive — uma aba, várias
 * seções), Serviços e Colaboradores existem para toda empresa; as demais
 * dependem do papel dela na operação. Documentos, Dados bancários e Drive eram
 * abas próprias e viraram seções do Cadastro: são dados de quem a empresa é,
 * não operação — e três abas para três cartões é navegação demais para pouco
 * conteúdo. Os convites de auto-cadastro seguiram o mesmo raciocínio: eram uma
 * aba e viraram seção de Colaboradores, que é onde quem gere a equipe os
 * procura — e a aba de colaboradores é a mesma do perfil do cliente, para que
 * fornecedor, assessor e cliente não tenham três telas divergentes para a
 * mesma lista.
 *
 * ## A aba inicial depende do papel
 *
 * Fornecedor e assessor abrem em **Relatórios**: quem clica num fornecedor na
 * carteira quer saber como foi o ano dele, não quem trabalha nele. Uma empresa
 * sem papel na operação abre no Cadastro. Como o papel é consulta assíncrona, a
 * escolha é derivada — fixar uma aba antes da resposta faria a tela piscar de
 * uma aba para outra.
 *
 * ## Quem não é admin
 *
 * Chega aqui pelas próprias empresas (Gestão → Empresas) e vê o que é dele: o
 * Cadastro — editável pelo gestor, com conta bancária e documentos — e, para o
 * gestor, os Colaboradores, onde ele cadastra a equipe e define quem é gestor e
 * quem assina. Relatórios, serviços, deságio e CRM são da operação: os números
 * do ano dele estão no Painel. A API confere tudo de novo.
 */
export function EmpresaDetalhe(): JSX.Element {
  const { businessId } = useParams<{ businessId: string }>();
  const [parametros] = useSearchParams();
  // Quem veio de uma carteira volta para ela; ver `origem-da-empresa.ts`.
  const origem = ORIGENS[origemDaQuery(parametros.get(PARAMETRO_DE_ORIGEM))];
  const [abaEscolhida, setAba] = useState<Aba | null>(null);
  const identidade = useIdentidade();
  const admin = identidade?.isAdmin === true;
  // O gestor da própria empresa edita o cadastro e cuida dos colaboradores.
  const gestor =
    identidade?.vinculos.some(
      (v) => v.businessId === businessId && v.papeis.includes('BUSINESS_OWNER'),
    ) === true;

  const empresa = useQuery({
    queryKey: ['empresa', businessId],
    queryFn: () => obterEmpresa(businessId as string),
    enabled: businessId !== undefined,
  });

  // Os papéis de ICMS decidem quais abas existem. Consulta separada porque o
  // cadastro da empresa não sabe de ICMS — são dois domínios.
  const papeis = useQuery({
    queryKey: ['icms-papeis', businessId],
    queryFn: () =>
      requisicaoAutenticada<PapeisDeIcms>(`/icms/settings/business/${businessId ?? ''}`),
    // Os papéis só abrem abas da operação; quem não é admin não as tem.
    enabled: businessId !== undefined && admin,
  });

  if (empresa.isLoading || papeis.isLoading) return <EstadoCarregando contexto="a empresa" />;
  if (empresa.isError) {
    return (
      <EstadoErro
        titulo="Não foi possível carregar a empresa"
        mensagem={empresa.error.message}
        aoTentarNovamente={() => void empresa.refetch()}
      />
    );
  }

  const dados = empresa.data;
  if (dados === undefined || businessId === undefined) {
    return <EstadoVazio titulo="Empresa não encontrada" />;
  }

  const ehFornecedor = admin && papeis.data?.papeis.fornecedor === true;
  const ehAssessor = admin && papeis.data?.papeis.assessor === true;
  const participa = ehFornecedor || ehAssessor;
  const aba: Aba = abaEscolhida ?? (participa ? 'relatorio' : 'cadastro');

  return (
    <Pagina
      titulo={dados.legalName}
      descricao={`CNPJ ${formatarCnpj(dados.taxpayerId)} · ${dados.emailAddress}`}
      // Quem não é admin chega aqui pela empresa escolhida, não por uma lista:
      // não há para onde "voltar" — trocar de empresa é no topo.
      {...(admin
        ? {
            acao: (
              <div className="flex flex-col items-end gap-3">
                <Link
                  to={origem.rota}
                  className="text-sm text-conteudo-secundario underline-offset-4 hover:text-conteudo-primario hover:underline"
                >
                  ← Voltar para {origem.rotulo.toLowerCase()}
                </Link>
                <BotaoDoCrm businessId={businessId} ativa={dados.isActive} />
              </div>
            ),
          }
        : {})}
    >
      {admin && (
        <nav aria-label="Trilha" className="text-sm text-conteudo-sutil">
          <Link
            to={origem.rota}
            className="underline-offset-4 hover:text-conteudo-primario hover:underline"
          >
            {origem.rotulo}
          </Link>
          <span aria-hidden="true" className="mx-2">
            ›
          </span>
          <span className="text-conteudo-secundario">{dados.legalName}</span>
        </nav>
      )}

      {!dados.isActive && (
        <Aviso tom="atencao" titulo="Empresa desativada">
          Este cadastro está inativo. Ele não aparece nas listagens da operação e os vínculos de
          colaboradores foram encerrados junto.
        </Aviso>
      )}

      <div role="group" aria-label="Cadastros da empresa" className="flex flex-wrap gap-2">
        {/*
          Relatório existe para fornecedor **e** assessor: os dois têm carteira
          e receita, e a pergunta "como foi meu ano" é a mesma. Vem primeiro
          porque é a aba inicial deles. Os assessores e os clientes do
          fornecedor ficam dentro do Relatório, filtrados pelo mesmo exercício
          — por isso o fornecedor não tem aba "Clientes" (lista de 15/09, item
          17). O assessor ainda tem: a carteira dele é cruzada por fornecedor.
        */}
        {participa && (
          <Aba ativo={aba === 'relatorio'} aoClicar={() => setAba('relatorio')}>
            Relatórios
          </Aba>
        )}
        {ehAssessor && !ehFornecedor && (
          <Aba ativo={aba === 'clientes'} aoClicar={() => setAba('clientes')}>
            Clientes
          </Aba>
        )}
        <Aba ativo={aba === 'cadastro'} aoClicar={() => setAba('cadastro')}>
          Cadastro
        </Aba>
        {/* A aba de serviços só existia no perfil do cliente — fornecedor e
            assessor também contratam produtos da operadora. */}
        {admin && (
          <Aba ativo={aba === 'servicos'} aoClicar={() => setAba('servicos')}>
            Serviços
          </Aba>
        )}
        {(admin || gestor) && (
          <Aba ativo={aba === 'colaboradores'} aoClicar={() => setAba('colaboradores')}>
            Colaboradores
          </Aba>
        )}
        {ehAssessor && (
          <Aba ativo={aba === 'desagio'} aoClicar={() => setAba('desagio')}>
            Deságio de compra
          </Aba>
        )}
        {/*
          Modelos de contrato não têm aba própria: moram em Serviços, porque
          são dos serviços prestados (lista de 21/09) — um ou mais por
          serviço, e quem fecha escolhe qual usar.
        */}
      </div>

      {aba === 'cadastro' && (
        <AbaCadastro
          businessId={businessId}
          rotaAposDesativar="/empresas"
          {...(admin ? {} : { modo: gestor ? 'gestao' : 'leitura' })}
        />
      )}
      {aba === 'servicos' && admin && <AbaServicos businessId={businessId} />}
      {aba === 'colaboradores' && (admin || gestor) && (
        <ColaboradoresDaEmpresa businessId={businessId} razaoSocial={dados.legalName} />
      )}
      {aba === 'desagio' && <DesagioDoAssessor assessorId={businessId} />}
      {/*
        Uma empresa pode ser fornecedora **e** assessora. O relatório abre pelo
        papel de fornecedor nesse caso: é o que tem mais número para mostrar.
      */}
      {aba === 'relatorio' && participa && (
        <RelatorioDoParticipante
          businessId={businessId}
          papel={ehFornecedor ? 'fornecedor' : 'assessor'}
        />
      )}
      {aba === 'clientes' && ehAssessor && !ehFornecedor && (
        <ClientesDoParticipante businessId={businessId} papel="assessor" />
      )}
    </Pagina>
  );
}

/* ─── Colaboradores ───────────────────────────────────────────────────────── */

/**
 * A aba de colaboradores é a do perfil do cliente.
 *
 * Esta tela tinha uma tabela própria, com formulário de "Cargo" em texto livre
 * — que a API passou a recusar, porque o cronograma escolhe destinatários pelo
 * vínculo trabalhista e um cargo digitado à mão nunca casava com "Decisor" nem
 * "Contábil". Em vez de consertar a cópia, usa-se a original: mesmas colunas,
 * mesmos formulários, mesma seção de convites — e o mesmo "Extrair xlsx", na
 * linha dos botões dela.
 */
function ColaboradoresDaEmpresa(props: {
  readonly businessId: string;
  readonly razaoSocial: string;
}): JSX.Element {
  return <AbaColaboradores businessId={props.businessId} razaoSocial={props.razaoSocial} />;
}

/* ─── Peças ───────────────────────────────────────────────────────────────── */

function Aba(props: {
  readonly ativo: boolean;
  readonly aoClicar: () => void;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={props.ativo}
      onClick={props.aoClicar}
      className={`inline-flex h-9 items-center rounded-card border px-4 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano ${
        props.ativo
          ? 'border-acento-ciano/50 bg-acento-ciano/10 font-medium text-acento-ciano'
          : 'border-borda text-conteudo-secundario hover:text-conteudo-primario'
      }`}
    >
      {props.children}
    </button>
  );
}
